import { CriticalError } from './CriticalError';

interface ErrorBoundaryFallbackProps {
  error: Error;
  /** Where it threw, e.g. "EventTable < ReconstitutionSchedule < Steps". */
  detail?: string;
  reset?: () => void;
  className?: string;
}

/**
 * Fallback UI rendered when a React Error Boundary or TanStack Router
 * errorComponent catches an unhandled exception.
 */
export const ErrorBoundaryFallback = ({
  error,
  detail,
  reset,
  className,
}: ErrorBoundaryFallbackProps) => {
  return (
    <CriticalError
      title="Something went wrong"
      message="This section encountered an unexpected error. The rest of the application is unaffected."
      // Dev only, same as the message: in production this is an internal
      // detail, and the person who needs it reads it from the console log.
      code={
        import.meta.env.DEV
          ? [error.message, detail].filter(Boolean).join(' — ')
          : undefined
      }
      buttonText="Try Again"
      // `reset` was accepted here and then dropped, so "Try Again" fell through
      // to CriticalError's default full-page reload — which throws away every
      // unsaved form, dialog and in-flight query on the page to recover from a
      // fault that is usually transient. Re-rendering the subtree is the cheap
      // attempt; CriticalError still reloads when no reset is available, and
      // if the render throws again the boundary simply catches it again.
      onRetry={reset}
      className={className}
    />
  );
};
